import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Menu, X, User, ChevronDown, Sparkles } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import CurrencySelector from "./CurrencySelector";
import NavSearch from "./NavSearch";
import ReassuranceBar from "./ReassuranceBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const { count, setIsOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loyaltyPoints, setLoyaltyPoints] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fetch loyalty balance for logged-in customers (widget in navbar)
  useEffect(() => {
    if (!user) { setLoyaltyPoints(null); return; }
    let cancelled = false;
    axios.get(`${API}/loyalty/me`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setLoyaltyPoints(data?.points ?? 0); })
      .catch(() => { if (!cancelled) setLoyaltyPoints(null); });
    return () => { cancelled = true; };
  }, [user]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${scrolled ? "nav-glass" : "bg-black/40 backdrop-blur-sm"}`}
    >
      <ReassuranceBar />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <button
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <nav className="hidden md:flex items-center gap-8 label-caps text-white/70">
          {/* Parfums dropdown */}
          <div className="relative group" data-testid="nav-parfums-group">
            <button
              data-testid="nav-parfums"
              className="flex items-center gap-1 hover:text-[color:var(--gold)] transition-colors py-2"
            >
              Parfums <ChevronDown size={12} className="mt-0.5 opacity-70 group-hover:opacity-100 transition-opacity" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="bg-[#0A0A0A] border border-white/15 min-w-[220px] py-2">
                <Link
                  to="/collection/signature"
                  data-testid="nav-signature"
                  className="block px-5 py-3 text-white/70 hover:text-[color:var(--gold)] hover:bg-white/5 transition-colors"
                >
                  Signature
                  <span className="block text-[9px] text-white/40 tracking-normal normal-case mt-0.5">Parfums européens</span>
                </Link>
                <Link
                  to="/collection/orient"
                  data-testid="nav-orient"
                  className="block px-5 py-3 text-white/70 hover:text-[color:var(--gold)] hover:bg-white/5 transition-colors border-t border-white/5"
                >
                  Orient
                  <span className="block text-[9px] text-white/40 tracking-normal normal-case mt-0.5">Dubaï & Moyen-Orient</span>
                </Link>
              </div>
            </div>
          </div>
          {/* L'Art de Vivre — regroupe montres, lunettes, sacs (accessoires) */}
          <div className="relative group">
            <button data-testid="nav-artdevivre" className="hover:text-[color:var(--gold)] transition-colors flex items-center gap-1">
              L'Art de Vivre
              <ChevronDown size={12} className="opacity-60 group-hover:opacity-100" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="bg-black border border-[color:var(--gold)]/20 min-w-[220px]">
                <Link
                  to="/#montres"
                  data-testid="nav-montres"
                  className="block px-5 py-3 text-white/70 hover:text-[color:var(--gold)] hover:bg-white/5 transition-colors"
                >
                  Montres
                  <span className="block text-[9px] text-white/40 tracking-normal normal-case mt-0.5">Sélection horlogère</span>
                </Link>
                <Link
                  to="/lunettes"
                  data-testid="nav-lunettes"
                  className="block px-5 py-3 text-white/70 hover:text-[color:var(--gold)] hover:bg-white/5 transition-colors border-t border-white/5"
                >
                  Lunettes
                  <span className="block text-[9px] text-white/40 tracking-normal normal-case mt-0.5">Solaires d'exception</span>
                </Link>
                <Link
                  to="/sacs"
                  data-testid="nav-sacs"
                  className="block px-5 py-3 text-white/70 hover:text-[color:var(--gold)] hover:bg-white/5 transition-colors border-t border-white/5"
                >
                  Sacs
                  <span className="block text-[9px] text-white/40 tracking-normal normal-case mt-0.5">Maroquinerie de prestige</span>
                </Link>
              </div>
            </div>
          </div>
          <Link to="/a-propos" data-testid="nav-about" className="hover:text-[color:var(--gold)] transition-colors">La Maison</Link>
          <Link to="/contact" data-testid="nav-contact" className="hover:text-[color:var(--gold)] transition-colors">Contact</Link>
        </nav>

        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img
            src="/logo-maison-douba.png"
            alt="MAISON DOUBA — Paris"
            className={`transition-all duration-500 object-contain ${scrolled ? "h-12 md:h-14" : "h-16 md:h-20"}`}
          />
        </Link>

        <div className="flex items-center gap-6 text-white/80">
          <div className="hidden md:block">
            <NavSearch />
          </div>
          <Link
            to={user ? "/compte" : "/connexion"}
            data-testid="nav-account"
            className="hidden md:block hover:text-[color:var(--gold)] transition-colors"
            aria-label={user ? "Mon compte" : "Se connecter"}
          >
            <User size={18} strokeWidth={1.5} />
          </Link>
          {user && loyaltyPoints !== null && loyaltyPoints > 0 && (
            <Link
              to="/compte?tab=loyalty"
              data-testid="nav-loyalty-widget"
              className="hidden md:flex items-center gap-1.5 px-3 py-1 border border-[color:var(--gold)]/40 hover:border-[color:var(--gold)] hover:bg-[color:var(--gold)]/[0.04] transition-colors group"
              aria-label="Mes points fidélité"
            >
              <Sparkles size={12} className="text-[color:var(--gold)] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[color:var(--gold)] text-[11px] tracking-[0.15em] font-serif">
                {loyaltyPoints} <span className="text-[9px] uppercase tracking-[0.25em] opacity-70">pts</span>
              </span>
            </Link>
          )}
          <div className="hidden md:block">
            <CurrencySelector compact />
          </div>
          <button
            data-testid="nav-cart"
            onClick={() => setIsOpen(true)}
            className="relative hover:text-[color:var(--gold)] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 bg-[color:var(--gold)] text-black text-[10px] font-medium w-4 h-4 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#050505] border-t border-white/10 px-6 py-6 label-caps">
          <p className="text-white/40 text-[10px] tracking-[0.25em] mb-2">Parfums</p>
          <Link to="/collection/signature" onClick={() => setMobileOpen(false)} className="block py-2 pl-3 text-white/80" data-testid="mobile-signature">Collection Signature</Link>
          <Link to="/collection/orient" onClick={() => setMobileOpen(false)} className="block py-2 pl-3 text-white/80" data-testid="mobile-orient">Collection Orient</Link>
          <div className="border-t border-white/10 my-3" />
          <p className="text-white/40 text-[10px] tracking-[0.25em] mb-2">L'Art de Vivre</p>
          <a href="/#montres" onClick={() => setMobileOpen(false)} className="block py-2 pl-3 text-white/80">Montres</a>
          <Link to="/lunettes" onClick={() => setMobileOpen(false)} className="block py-2 pl-3 text-white/80" data-testid="mobile-lunettes">Lunettes</Link>
          <Link to="/sacs" onClick={() => setMobileOpen(false)} className="block py-2 pl-3 text-white/80" data-testid="mobile-sacs">Sacs</Link>
          <div className="border-t border-white/10 my-3" />
          <Link to="/a-propos" onClick={() => setMobileOpen(false)} className="block py-2 text-white/80">La Maison</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2 text-white/80">Contact</Link>
          <Link to={user ? "/compte" : "/connexion"} onClick={() => setMobileOpen(false)} className="block py-3 text-[color:var(--gold)]" data-testid="mobile-account">
            {user ? "Mon compte" : "Connexion / Inscription"}
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
